import { urlify } from '../utils/string';

import {
	CHANGE_DEVICE,
	COOKIE_CONSENT,
	INIT,
	SITE_INIT,
	OPEN_MENU,
	OPEN_CART,
	SET_QUERY,
	REMOVE_NOTIFICATION,
	CHANGE_LANGUAGE,
	CHANGE_COUNTRY,
	UPDATE_EVENT_DETAILS,
	UPDATE_EVENT_DETAILS_ERRORS,
	PARTICIPANTS_REGISTERED,
	PARTICIPANTS_ERROR,
	ACUITY_IFRAME_HEIGHT,
} from '../actions/environment';
import { setCookie, getCookie, addClass, removeClass } from '../utils/dom';

const initialState = {
	properties: {
		openCart: false,
		siteInitiated: false,
		siteUpdated: false,
	},
	cookieConsent: ['default'],
	setup: {},
	header: {},
	footer: {},
	searchInputText: '',
	hashLocation: '',
	hostname: '',
	navigation: {
		open: false,
	},
	loadedCategories: [],
	acuityIframeHeight: 1100,
	pagination: {},
	favourites: [],
	eventDetails: {
		allowFriends: false,
		subscribeToNewsletter: false,
		acceptTerms: false,
		serverErrors: [],
		errors: {},
	},
};

if (typeof document !== 'undefined') initialState.cookieConsent = getCookie('cookies');

const environment = (state = initialState, action) => {
	switch (action.type) {
		case INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteClosed: action.initData.siteClosed || false,
					siteInitiated: true,
				},
				setup: {
					...state.setup,
					...action.initData.setup,
				},
				header: {
					...state.header,
					...action.initData.header,
				},
				magazineSnippets: action.initData.magazineSnippets,
				footer: {
					...state.footer,
					...action.initData.footer,
				},
				navigation: {
					...state.navigation,
					...action.initData.navigation,
				},
			};

		case CHANGE_DEVICE:
			return {
				...state,
				setup: {
					...state.setup,
					breakPoint: action.breakPoint,
				},
			};

		case COOKIE_CONSENT:
			return {
				...state,
				cookieConsent: action.consent.slice(),
			};

		case ACUITY_IFRAME_HEIGHT:
			return {
				...state,
				acuityIframeHeight: action.height,
			};

		case 'SITE_UPDATED':
			return {
				...state,
				properties: {
					...state.properties,
					siteUpdated: true,
				},
			};

		case 'UPDATE_PAGINATION':
			return {
				...state,
				pagination: {
					...state.pagination,
					[action.currentPath]: action.currentPage,
				},
			};

		case 'APPEND_CATEGORY':
			return {
				...state,
				loadedCategories: Array.isArray(action.category) ? [...action.category] : [...state.loadedCategories, action.category],
			};

		case 'UPDATE_HASH':
			return {
				...state,
				hashLocation: action.currentHash || action.hash,
			};

		case SITE_INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteInitiated: true,
				},
			};

		case CHANGE_LANGUAGE: {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [action.language, state.setup.country, parseInt(state.setup.market, 10), parseInt(state.setup.pricelist, 10)]);
			}
			return {
				...state,
				setup: {
					...state.setup,
					language: action.language,
				},
			};
		}

		case CHANGE_COUNTRY: {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [state.setup.language, action.country, parseInt(action.market, 10), parseInt(action.pricelist, 10)]);
			}
			return {
				...state,
				setup: {
					...state.setup,
					country: action.country,
					market: action.market,
					pricelist: action.pricelist,
					currency: action.currency,
				},
			};
		}

		case OPEN_MENU: {
			const open = action.open !== undefined ? action.open : !state.navigation.open;
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				if (open) addClass(document.body, 'u_menuIsOpen');
				else removeClass(document.body, 'u_menuIsOpen');
			}
			return {
				...state,
				navigation: {
					...state.navigation,
					open,
				},
			};
		}

		case OPEN_CART:
			return {
				...state,
				properties: {
					...state.properties,
					openCart: action.open !== undefined ? action.open : !state.properties.openCart,
				},
			};

		case SET_QUERY:
			return {
				...state,
				setup: {
					...state.setup,
					[action.key]: action.value,
				},
			};

		case REMOVE_NOTIFICATION:
			return {
				...state,
				header: {
					...state.header,
					[action.notificationType]: (
						state.header[action.notificationType].map((notification) => {
							if (urlify(notification.name) === action.notificationName) {
								return {
									...notification,
									removed: true,
								};
							}
							return notification;
						})
					),
				},
			};

		case 'SEARCH':
			return {
				...state,
				searchInputText: action.text,
			};

		case 'TOGGLE_FAVOURITE': {
			let favourites = [...state.favourites];
			const index = favourites.indexOf(action.productId);

			if (index === -1) {
				favourites = [...favourites, action.productId];
			} else {
				favourites = [...favourites.slice(0, index), ...favourites.slice(index + 1)];
			}

			localStorage.setItem('favourites', JSON.stringify(favourites));

			return {
				...state,
				favourites,
			};
		}

		case 'UPDATE_FAVOURITES':
			return {
				...state,
				favourites: [...action.favourites],
			};

		case UPDATE_EVENT_DETAILS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					[action.id]: action.value ? action.value : !state.eventDetails[action.id],
					eventRegistrationFailed: false,
					eventRegistrationSucceded: false,
					serverErrors: [],
				},
			};

		case UPDATE_EVENT_DETAILS_ERRORS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					errors: {
						...state.eventDetails.errors,
						[action.id]: action.error,
					},
				},
			};

		case PARTICIPANTS_REGISTERED:
			return {
				...state,
				eventDetails: {
					...initialState.eventDetails,
					eventRegistrationSucceded: true,
					eventRegistrationFailed: false,
					serverErrors: [],
				},
			};

		case PARTICIPANTS_ERROR:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					eventRegistrationFailed: true,
					eventRegistrationSucceded: false,
					serverErrors: action.errors ? [...action.errors] : [],
				},
			};

		default:
			return state;
	}
};

export { initialState as environmentState };
export default environment;
